import React, { useMemo } from 'react';
import { Box, Text, useAutoFontSize } from '@common-fe/common-fe';

import { AccentText } from '@/components';

import { useCurrentOrganization } from '../../hooks';

import BannerAnimation from './BannerAnimation';
import BannerReplenishmentPreview from './BannerReplenishmentPreview';
import EmployeesBlock from './EmployeesBlock';
import EnrolledBlock from './EnrolledBlock';
import { useEnrolledBlockQuery, useSubstantiationRateQuery, useTotalEmployeesQuery } from './queries';

interface Props {}
const Banner: React.FC<Props> = () => {
  const { defaultOrganizationData, observingOrganization } = useCurrentOrganization();

  const orgName = useMemo(
    () => observingOrganization?.name || defaultOrganizationData?.name || '',
    [defaultOrganizationData?.name, observingOrganization?.name]
  );
  const { containerRef, fontSize } = useAutoFontSize(orgName, {
    maxFontSize: 72,
  });
  const { isEmpty: isEmptySubstantiationRate } = useSubstantiationRateQuery();
  const {
    isLoading: isEmployeesLoading,
    total: employeesTotal,
    isEmpty: isEmployeesTotal,
  } = useTotalEmployeesQuery();
  const {
    isLoading: isEnrolledBlockLoading,
    data: enrolledDataBlock,
    isEmpty: isEnrolledEmpty,
  } = useEnrolledBlockQuery();

  return (
    <Box data-testid="dashboard_banner">
      <Box
        data-testid="dashboard_banner_container"
        background="onDarkBackground"
        pad="spacing32"
        direction="row"
        overflow="hidden"
        round="moduleRound"
        style={{ position: 'relative' }}
      >
        <BannerAnimation />
        <Box
          data-testid="dashboard_banner_welcome"
          style={{ flex: 1, zIndex: 2 }}
          height={{ min: '250px' }}
          justify="center"
        >
          <Text
            data-testid="dashboard_banner_welcome-text"
            color="textOnColor"
            weight={400}
            style={{ opacity: 0.5 }}
          >
            Welcome Home
          </Text>

          <Box
            margin={{ top: 'spacing8', right: 'spacing48' }}
            ref={containerRef as React.Ref<HTMLDivElement>}
          >
            <AccentText
              color="textOnColor"
              weight={700}
              data-testid="dashboard_banner_org-name"
              style={{ lineHeight: 1, fontWeight: 700 }}
              fontSize={fontSize || 32}
            >
              {orgName}
            </AccentText>
          </Box>
        </Box>

        <Box
          data-testid="dashboard_banner_stats"
          direction="row"
          align="stretch"
          height={{ min: '250px' }}
          style={{ flex: 1, zIndex: 2 }}
        >
          <BannerReplenishmentPreview />

          {(!isEnrolledEmpty || !isEmployeesTotal) && (
            <Box data-testid="dashboard_banner_blocks" style={{ flex: 1 }} direction="row">
              {!isEnrolledEmpty && (
                <Box style={{ flex: 1 }} margin={{ right: '1px' }}>
                  <EnrolledBlock
                    isLoading={isEnrolledBlockLoading || isEmployeesLoading}
                    data={enrolledDataBlock}
                  />
                </Box>
              )}
              {!isEmployeesTotal && (
                <Box style={{ flex: 1 }} margin={{ left: '1px' }}>
                  <EmployeesBlock
                    total={employeesTotal}
                    isLoading={isEmployeesLoading}
                    isWideMode={isEmptySubstantiationRate || isEnrolledEmpty}
                  />
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};
export default Banner;
